import type { Moment } from 'moment'
import type { FormEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import {
  createTripEvent,
  createTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextInput from 'components/Common/Form/Input/TextInput'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import InputText from 'LuxKit/Typography/InputText'
import noop from 'lib/function/noop'
import type { CreateTripError } from 'tripPlanner/api/types'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { useCreateTrip, useEditableTrips } from 'tripPlanner/hooks/api/trip'
import type { FullTrip } from 'tripPlanner/types/common'
import { getDefaultTripImageId } from 'tripPlanner/utils'

interface Props {
  startDate?: Moment
  endDate?: Moment
  onCreateTrip: (trip: FullTrip) => void
  onCancel: () => void
}

const CharCount = styled(InputText)`
  position: absolute;
  bottom: -4px;
  transform: translateY(100%);
  right: 0px;
`

function CreateTripStart({
  startDate,
  endDate,
  onCreateTrip,
  onCancel: onCancelProp,
}: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { data: trips } = useEditableTrips({ onError: noop, onSuccess: noop })

  const isFirstTrip = !!trips && trips.length === 0

  const { mutate: createTrip, isLoading } = useCreateTrip({
    onSuccess: (trip) => {
      onCreateTrip(trip)
      if (isFirstTrip) {
        fireInteractionEvent(createTripSaveEvent(trip.name, false))
      }
    },
    onError: (e: CreateTripError) => {
      setErrorMsg(e.message)
    },
  })

  const onCreate = useCallback(() => {
    if (name.length) {
      const imageId = getDefaultTripImageId(trips)

      createTrip({
        name,
        startDate,
        endDate,
        imageId,
      })
    }
  }, [endDate, trips, createTrip, name, startDate])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const setEmptyTripNameError = useCallback(() => {
    setErrorMsg(
      intl.formatMessage({
        id: '3485R_QMwhZgShLhHAt0',
        defaultMessage: 'Trip name cannot be empty',
        description: 'Error message shown when trip name is empty in the trip planner',
      }),
    )
  }, [intl])

  const createTripPlan = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (!isLoading) {
        if (name.length > 0) {
          onCreate()
        } else {
          setEmptyTripNameError()
        }
      }
    },
    [isLoading, name.length, onCreate, setEmptyTripNameError],
  )

  const onChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const name = e.target.value
      setName(name)
      if (name.length === 0) {
        setEmptyTripNameError()
      } else if (errorMsg) {
        setErrorMsg('')
      }
    },
    [errorMsg, setEmptyTripNameError],
  )

  const onCancel = useCallback(() => {
    if (isFirstTrip) {
      fireInteractionEvent(
        createTripEvent(
          'modal_first_trip_name',
          'global',
          'dismiss',
          true,
          false,
        ),
      )
    }
    onCancelProp()
  }, [isFirstTrip, onCancelProp])

  const modalTitle = intl.formatMessage({
    id: '551pHtct33X1Rj1IRPrv',
    defaultMessage: 'Name your new trip',
    description: 'Title for the create trip modal in the trip planner feature',
  })

  const tripNamePlaceholder = intl.formatMessage({
    id: 'f1E9ubwfI9V0HKmAga39',
    defaultMessage: 'e.g. Bali Trip',
    description: 'Placeholder text for trip name input field in the trip planner',
  })

  const tripNameLabel = intl.formatMessage({
    id: 'pvM0qB8ik9qSMOhrvQuP',
    defaultMessage: 'Trip name',
    description: 'Label for trip name input field in the trip planner feature',
  })

  const createTripButtonText = intl.formatMessage({
    id: '0BdluvBTG76Lzj7d-U5S',
    defaultMessage: 'Create trip',
    description: 'Button text to create a new trip in the trip planner feature',
  })

  const cancelButtonText = intl.formatMessage({
    id: 'RhnrNCNf9GRCp5gLlft4',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel trip creation in the trip planner feature',
  })

  return (
    <form onSubmit={createTripPlan}>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={onCancel}
      />
      <ModalBody>
        <ModalContent>
          <TextInput
            placeholder={tripNamePlaceholder}
            label={tripNameLabel}
            ref={inputRef}
            width="100%"
            onChange={onChangeName}
            invalidErrorMessage={errorMsg}
            manualError
            maxLength={MAX_TRIP_NAME_CHARS}
            data-testid="trip-popup-trip-input"
            required
          >
            <CharCount variant="caption" colour="neutral-three">
              {intl.formatMessage(
                {
                  id: 'dXYufAPJOwP6CvvEcu3e',
                  defaultMessage: '{current}/{max}',
                  description: 'Character count indicator for the trip name input when creating a new trip in trip planner',
                },
                { current: name.length, max: MAX_TRIP_NAME_CHARS },
              )}
            </CharCount>
          </TextInput>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          type: 'submit',
          children: createTripButtonText,
          disabled: isLoading,
        }}
        secondaryActionProps={{
          children: cancelButtonText,
          disabled: isLoading,
          onClick: onCancel,
        }}
      />
    </form>
  )
}

export default CreateTripStart
